import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const LibertadoresAIPicks2025Page = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const semifinalistas2025 = [
    {
      time: 'Palmeiras',
      pais: 'Brasil',
      forma: '9V 1E 0D',
      chanceIA: 'ALTA',
      gols: '26 gols em 10 jogos (2.6/jogo)',
      defesa: '6 sofridos (0.6/jogo), 6 jogos sem levar',
      jogadorChave: 'José López (7 gols, 1 assist)',
      destaque: 'Favorito absoluto apesar do déficit'
    },
    {
      time: 'LDU de Quito',
      pais: 'Equador',
      forma: '6V 2E 2D',
      chanceIA: 'MÉDIA-ALTA',
      gols: '13 marcados/5 sofridos em 10 jogos',
      defesa: 'Invicto em casa (5V, 1E, 0D), 7 jogos sem levar',
      jogadorChave: 'Álex Arce (4 gols, 1 assist)',
      destaque: 'A zebra: pode eliminar o favorito'
    },
    {
      time: 'Racing Club',
      pais: 'Argentina',
      forma: '7V 1E 3D',
      chanceIA: 'MÉDIA',
      gols: '19 marcados, 6 sofridos',
      defesa: '6 jogos sem levar gol',
      jogadorChave: 'Adrián Martínez (7 gols)',
      destaque: 'Melhor time argentino, perigoso no final'
    },
    {
      time: 'Flamengo',
      pais: 'Brasil',
      forma: '7V 3E 1D',
      chanceIA: 'MÉDIA',
      gols: '13 marcados, 5 sofridos',
      defesa: 'Sólida, jogos equilibrados',
      jogadorChave: 'Luiz Araújo (3 assistências)',
      destaque: 'Consistente mas menos gols que rivais'
    }
  ];

  const estrategiasIA = [
    {
      time: 'Palmeiras',
      estrategia: 'Aposte na virada - Jogo de volta em casa',
      reasoning: 'Apesar de perder 3-0, têm histórico de viradas heroicas, melhor ataque (26 gols), flexibilidade tática de Abel Ferreira.'
    },
    {
      time: 'LDU de Quito',
      estrategia: 'Value em Underdog - Aposte em casa e unders',
      reasoning: 'Invictos em casa, defesa de aço (7 clean sheets), vantagem da altitude. Se segurar Palmeiras, podem chegar na final.'
    },
    {
      time: 'Racing Club',
      estrategia: 'Aposte em gols tardios e mercados ao vivo',
      reasoning: '47% dos gols nos últimos 15 minutos. Martínez (7 gols) é finalizador letal. Excelente para apostas ao vivo.'
    },
    {
      time: 'Flamengo',
      estrategia: 'Under de gols e props defensivos',
      reasoning: 'Jogos equilibrados (13 marcados/5 sofridos), raramente levam goleada. Controle > explosão. Aposte em unders.'
    }
  ];

  const faqs = [
    {
      q: "Quais os 4 semifinalistas da Libertadores 2025?",
      a: "Os 4 times nas semifinais são: Palmeiras (Brasil), LDU de Quito (Equador), Racing Club (Argentina) e Flamengo (Brasil). Palmeiras e LDU estão na primeira semi, Racing e Flamengo na segunda."
    },
    {
      q: "Quem é o favorito para ganhar a Libertadores 2025?",
      a: "Palmeiras é o favorito absoluto segundo a IA, com melhor ataque (26 gols) e defesa sólida. Apesar de perder 3-0 para LDU na ida, têm histórico de viradas e jogam a volta em casa. LDU de Quito é a zebra com alta probabilidade se segurar o resultado."
    },
    {
      q: "LDU pode eliminar o Palmeiras perdendo 3-0?",
      a: "Sim! LDU tem defesa de aço (7 clean sheets), é invicto em casa (5V 1E 0D) e tem vantagem da altitude de Quito. Se segurarem a pressão do Palmeiras no jogo de volta, podem fazer história."
    },
    {
      q: "Qual a melhor estratégia de apostas para as semifinais?",
      a: "Palmeiras: aposte na virada no jogo de volta. LDU: value em underdog e unders. Racing: gols tardios (47% após 75min). Flamengo: unders e mercados defensivos. PickStar AI ajusta em tempo real."
    }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Palpites da IA para a Libertadores 2025 - PickStar AI",
    "description": "Análise completa da Libertadores 2025 com IA. Favoritos, zebras, estratégias e os melhores palpites para cada fase da competição.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-10-23",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/libertadores-ai-picks-2025"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Palpites da IA para Libertadores 2025 | Sites de Apostas Copa Libertadores"
        description="Palpites certeiros da IA para a Copa Libertadores 2025. Análise completa dos favoritos, zebras e melhores estratégias de apostas com PickStar AI."
        keywords="libertadores 2025, palpites libertadores, site de apostas libertadores, sites de apostas copa libertadores, palpites ia libertadores, favoritos libertadores 2025, apostas libertadores"
        canonical="https://pickstarai.com/libertadores-ai-picks-2025"
        ogImage="https://pickstarai.com/og-home-logo.png"
        structuredData={[structuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                Libertadores 2025
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                Palpites da IA para a maior competição de clubes da América do Sul
              </p>
            </motion.div>
            <div className="mt-8">
              <button
                onClick={() => navigate('/chat')}
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                         text-[11px] font-medium text-white uppercase tracking-[0.15em]
                         hover:from-emerald-500 hover:to-violet-500 transition-all"
              >
                VER PALPITES DA IA
                <ChevronRight className="ml-2 w-3 h-3" />
              </button>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Semifinalistas */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">SEMIFINAIS</span>
              ANÁLISE DOS 4 SEMIFINALISTAS
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {semifinalistas2025.map((time, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-white/90">{time.time}</h3>
                      <p className="text-xs text-white/40 uppercase tracking-wider">{time.pais}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-emerald-400/80 uppercase tracking-wider">{time.chanceIA}</div>
                      <div className="text-[10px] text-white/30 uppercase tracking-wider">CHANCE IA</div>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm border-t border-white/[0.05] pt-4">
                    <div>
                      <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Forma:</span>
                      <p className="text-white/60 font-mono text-xs">{time.forma}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Gols:</span>
                      <p className="text-white/60 text-xs">{time.gols}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Defesa:</span>
                      <p className="text-white/60 text-xs">{time.defesa}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Jogador-Chave:</span>
                      <p className="text-white/60 text-xs">{time.jogadorChave}</p>
                    </div>
                    <div className="pt-3 border-t border-white/[0.05]">
                      <span className="text-xs text-emerald-400/80 font-medium">→ {time.destaque}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Estratégias por Time */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">ESTRATÉGIAS</span>
              COMO APOSTAR EM CADA TIME
            </h2>

            <div className="space-y-4">
              {estrategiasIA.map((dica, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <span className="inline-block px-3 py-1 bg-emerald-500/10 text-emerald-400/80 text-xs font-mono uppercase tracking-wider border border-emerald-500/20">
                        {dica.time}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">
                        {dica.estrategia}
                      </h3>
                      <p className="text-sm text-white/50">
                        <span className="text-white/70 font-medium">Por quê:</span> {dica.reasoning}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.q}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              ACOMPANHE A LIBERTADORES 2025 COM IA
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Receba palpites em tempo real para todos os jogos da Libertadores. Análise profunda, odds comparadas, estratégias vencedoras.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              COMEÇAR A USAR IA GRÁTIS
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default LibertadoresAIPicks2025Page;
